import type { ISelfReminderNotificationData } from "@frontend-shared/types";
import { Bell } from "lucide-react";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/lib/utils";

import { NotificationCardShell } from "../NotificationCardShell";
import { FLAT_MARKDOWN_HEADING_CLASSES } from "./constants";

interface ISelfReminderCardProps {
  uuid: string;
  data: ISelfReminderNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewRuns: (automationUuid: string, projectUuid?: string) => void;
}

const SelfReminderCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewRuns,
}: ISelfReminderCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");
  const [isExpanded, setIsExpanded] = useState(false);

  const handleViewRuns = useCallback((): void => {
    if (!data.automationUuid) {
      return;
    }
    onViewRuns(data.automationUuid, data.projectUuid);
  }, [data.automationUuid, data.projectUuid, onViewRuns]);

  const handleToggleExpanded = useCallback((): void => {
    setIsExpanded((wasExpanded) => !wasExpanded);
  }, []);

  // NOTE: NSAI-6309 — the backend stores `fullBody` only when the bell preview
  // actually dropped something, so its presence is exactly the condition for
  // offering the toggle. Notifications stored before that field existed have
  // no `fullBody` and keep rendering as they always did.
  const fullBody = data.fullBody;
  const renderedBody = isExpanded && fullBody ? fullBody : data.body;
  const expandLabel = isExpanded ? t("show_less") : t("show_more");

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="self_reminder"
      createdAt={createdAt}
      isRead={isRead}
      icon={<Bell className="h-5 w-5" />}
      ctaLabel={data.automationUuid ? t("view_runs") : undefined}
      onCtaClick={data.automationUuid ? handleViewRuns : undefined}
      secondaryCtaLabel={fullBody ? expandLabel : undefined}
      onSecondaryCtaClick={fullBody ? handleToggleExpanded : undefined}
      body={
        <div className="space-y-1">
          <p className="font-medium text-foreground">{data.title}</p>
          {/* NOTE: Scroll wide tables/code instead of clipping them
              (`[&_table]:block` is needed for table overflow to apply).
              NSAI-6309 — the expanded report is capped at `max-h-80` and
              scrolls inside the card, so a long report stays readable without
              pushing the rest of the drawer out of reach. */}
          <div
            data-testid="notification-card-self_reminder-body"
            data-expanded={isExpanded ? "true" : "false"}
            className={cn(
              "prose prose-sm dark:prose-invert max-w-none text-foreground/90 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_a]:text-primary [&_pre]:overflow-x-auto [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto",
              FLAT_MARKDOWN_HEADING_CLASSES,
              isExpanded && "max-h-80 overflow-y-auto",
            )}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {renderedBody}
            </ReactMarkdown>
          </div>
        </div>
      }
    />
  );
};

export const SelfReminderCard = memo(SelfReminderCardComponent);
